import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { ColorPalette } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { AttendanceHistoryItem } from '../types/attendance';
import type { HolidayRecord } from '../types/officeCalendar';
import {
  attendanceToneColor,
  attendanceWeekdayLabels,
  buildAttendanceCalendarDays,
  formatAttendanceMonthTitle,
  type AttendanceCalendarTone,
} from '../utils/attendanceCalendar';

type Props = {
  year: number;
  monthIndex: number;
  items: AttendanceHistoryItem[];
  holidays?: HolidayRecord[];
  selectedDate?: string | null;
  highlightMode?: 'fill' | 'dot';
  onSelectDate?: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
};

function Chevron({
  direction,
  color,
}: {
  direction: 'left' | 'right';
  color: string;
}) {
  return (
    <View
      style={[
        styles.chevron,
        direction === 'right' ? styles.chevronRight : null,
        { borderColor: color },
      ]}
      accessibilityElementsHidden
    />
  );
}

function dayDecoration(
  tone: AttendanceCalendarTone,
  selected: boolean,
  highlightMode: 'fill' | 'dot',
  palette: ColorPalette,
) {
  if (selected) {
    return {
      cellBackground: palette.primary,
      textColor: palette.onPrimary,
      showDot: false,
      dotColor: palette.transparent,
    };
  }
  if (tone === 'neutral') {
    return {
      cellBackground: palette.transparent,
      textColor: palette.textPrimary,
      showDot: false,
      dotColor: palette.transparent,
    };
  }
  const color = attendanceToneColor(tone, palette);
  if (highlightMode === 'dot') {
    return {
      cellBackground: palette.transparent,
      textColor: palette.textPrimary,
      showDot: true,
      dotColor: color,
    };
  }
  return {
    cellBackground: `${color}22`,
    textColor: color,
    showDot: tone === 'present',
    dotColor: color,
  };
}

export function AttendanceMonthCalendar({
  year,
  monthIndex,
  items,
  holidays = [],
  selectedDate,
  highlightMode = 'fill',
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: Props) {
  const palette = useAppColors();
  const days = useMemo(
    () => buildAttendanceCalendarDays(year, monthIndex, items, holidays),
    [holidays, items, monthIndex, year],
  );
  const weekdays = attendanceWeekdayLabels();
  const title = formatAttendanceMonthTitle(year, monthIndex);

  return (
    <View
      style={[
        styles.card,
        { backgroundColor: palette.surface, borderColor: palette.border },
      ]}
    >
      <View style={styles.monthRow}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Previous month"
          onPress={onPrevMonth}
          hitSlop={8}
          style={styles.monthNav}
        >
          <Chevron direction="left" color={palette.textPrimary} />
        </Pressable>
        <Text style={[styles.monthTitle, { color: palette.textPrimary }]}>
          {title}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Next month"
          onPress={onNextMonth}
          hitSlop={8}
          style={styles.monthNav}
        >
          <Chevron direction="right" color={palette.textPrimary} />
        </Pressable>
      </View>

      <View style={styles.weekdayRow}>
        {weekdays.map(label => (
          <Text
            key={label}
            style={[styles.weekday, { color: palette.textMuted }]}
          >
            {label}
          </Text>
        ))}
      </View>

      <View style={styles.grid}>
        {days.map((day, index) => {
          if (!day.inMonth) {
            return <View key={`empty-${index}`} style={styles.dayCell} />;
          }
          const selected = selectedDate === day.date;
          const deco = dayDecoration(
            day.tone,
            selected,
            highlightMode,
            palette,
          );
          return (
            <Pressable
              key={day.date}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`${day.date}${
                day.item
                  ? `, ${day.item.statusLabel}`
                  : day.holidayName
                    ? `, ${day.holidayName}`
                    : ''
              }`}
              onPress={() => onSelectDate?.(day.date)}
              style={styles.dayCell}
            >
              <View
                style={[
                  styles.dayBubble,
                  { backgroundColor: deco.cellBackground },
                ]}
              >
                <Text style={[styles.dayText, { color: deco.textColor }]}>
                  {day.day}
                </Text>
              </View>
              {deco.showDot ? (
                <View
                  style={[styles.statusDot, { backgroundColor: deco.dotColor }]}
                />
              ) : (
                <View style={styles.statusDotSpacer} />
              )}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.md,
  },
  monthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  monthNav: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    width: 10,
    height: 10,
    borderLeftWidth: 2,
    borderBottomWidth: 2,
    transform: [{ rotate: '45deg' }],
  },
  chevronRight: {
    transform: [{ rotate: '-135deg' }],
  },
  monthTitle: {
    ...typography.sectionTitle,
    fontWeight: '700',
  },
  weekdayRow: {
    flexDirection: 'row',
  },
  weekday: {
    ...typography.caption,
    flex: 1,
    textAlign: 'center',
    fontWeight: '600',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dayCell: {
    width: `${100 / 7}%`,
    alignItems: 'center',
    paddingVertical: spacing.xs,
    minHeight: 44,
  },
  dayBubble: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayText: {
    ...typography.caption,
    fontWeight: '700',
  },
  statusDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    marginTop: 2,
  },
  statusDotSpacer: {
    width: 5,
    height: 5,
    marginTop: 2,
  },
});
